/**
 * Continuous-Mic mit Voice-Activity-Detection.
 *
 * Verhalten:
 *  - start() öffnet das Mikrofon, startet MediaRecorder + AnalyserNode.
 *  - Solange aufgenommen wird, wird das Audio-Level (RMS) per rAF gesampled
 *    und über onLevel publiziert (Orb-Animation).
 *  - mode "continuous": VAD-Loop, Auto-Stop bei Stille → onAutoStop
 *  - mode "manual": kein Auto-Stop, Recorder läuft bis finish()/cancel()
 *    (Push-to-Talk-Modus für iOS-Sicherheit)
 *  - Wenn nach MAX_RECORD_MS niemand gesprochen hat, wird onTimeout gefeuert
 *    (nur im "continuous"-Modus).
 */

export interface ContinuousMicHandlers {
  onLevel(rms: number): void;
  onSpeechStart?(): void;
  onAutoStop(blob: Blob, filename: string): void;
  onTimeout?(): void;
  onError?(err: Error): void;
}

export interface ContinuousMicOptions {
  silenceMs?: number;
  speechThreshold?: number;
  onsetMs?: number;
  maxRecordMs?: number;
  mode?: "continuous" | "manual";
}

/** Browser-Detect: Safari (Desktop + iOS) bevorzugt audio/mp4 für MediaRecorder. */
function isSafariLike(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return isIOS || isSafari;
}

/** Liefert das beste vom Browser unterstützte MediaRecorder-MIME. */
function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const supported = (mime: string) =>
    typeof MediaRecorder.isTypeSupported === "function" &&
    MediaRecorder.isTypeSupported(mime);
  // Safari/iOS first: mp4 / mp4a sind dort die einzige zuverlässige Option.
  const safariFirst = [
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mpeg",
  ];
  const chromeFirst = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/mpeg",
  ];
  const candidates = isSafariLike() ? safariFirst : chromeFirst;
  for (const c of candidates) {
    if (supported(c)) return c;
  }
  return "";
}

/** Aus dem effektiven MIME-Typ eine Whisper-kompatible Datei-Endung ableiten. */
function extensionFor(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes("mp4") || m.includes("mp4a") || m.includes("aac")) return "m4a";
  if (m.includes("mpeg")) return "mp3";
  if (m.includes("ogg")) return "ogg";
  if (m.includes("wav")) return "wav";
  if (m.includes("webm")) return "webm";
  // Fallback: m4a wird von Whisper unterstützt und Safari liefert i. d. R. mp4-Bytes.
  return "m4a";
}

export class ContinuousMic {
  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private chunks: Blob[] = [];
  private rafId = 0;
  private effectiveMime = "";
  private suppressOnStop = false;
  private speechOnsetAt: number | null = null;
  private silenceStartAt: number | null = null;
  private hasSpoken = false;
  private startedAt = 0;
  private handlers: ContinuousMicHandlers | null = null;
  private mode: "continuous" | "manual" = "continuous";

  private readonly SILENCE_MS: number;
  private readonly SPEECH_THRESHOLD: number;
  private readonly ONSET_MS: number;
  private readonly MAX_RECORD_MS: number;

  constructor(opts: ContinuousMicOptions = {}) {
    this.SILENCE_MS = opts.silenceMs ?? 1500;
    this.SPEECH_THRESHOLD = opts.speechThreshold ?? 0.022;
    this.ONSET_MS = opts.onsetMs ?? 180;
    this.MAX_RECORD_MS = opts.maxRecordMs ?? 30_000;
    this.mode = opts.mode ?? "continuous";
  }

  async start(handlers: ContinuousMicHandlers): Promise<void> {
    if (this.recorder) return;
    this.handlers = handlers;
    this.suppressOnStop = false;

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      handlers.onError?.(
        new Error("Mikrofon-API nicht verfügbar (kein HTTPS oder veralteter Browser?)"),
      );
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      // Häufige iOS-Safari-Fehler kontextualisieren.
      const friendly =
        e.name === "NotAllowedError"
          ? "Mikrofon-Zugriff verweigert — Safari Einstellungen prüfen."
          : e.name === "NotFoundError"
            ? "Kein Mikrofon gefunden."
            : e.name === "SecurityError"
              ? "Mikrofon blockiert (HTTPS nötig)."
              : `${e.name || "Error"}: ${e.message}`;
      handlers.onError?.(new Error(friendly));
      return;
    }

    if (typeof MediaRecorder === "undefined") {
      this.cleanupStream();
      handlers.onError?.(new Error("MediaRecorder nicht verfügbar in diesem Browser."));
      return;
    }

    const chosen = pickMimeType();
    this.chunks = [];

    try {
      this.recorder = new MediaRecorder(
        this.mediaStream,
        chosen ? { mimeType: chosen } : undefined,
      );
    } catch (err) {
      // Letzter Versuch: ohne mimeType, Browser entscheidet.
      try {
        this.recorder = new MediaRecorder(this.mediaStream);
      } catch (err2) {
        const e = err2 instanceof Error ? err2 : new Error(String(err2));
        this.cleanupStream();
        handlers.onError?.(new Error(`MediaRecorder-Init: ${e.message}`));
        return;
      }
    }

    // Tatsächlichen MIME-Typ aus dem Recorder lesen (kann von chosen abweichen,
    // wenn Browser substituiert hat — Safari hat audio/mp4 manchmal als audio/mp4a-latm).
    this.effectiveMime = this.recorder.mimeType || chosen || "audio/webm";

    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.onstop = () => {
      const blobType = this.effectiveMime || "audio/webm";
      const blob = new Blob(this.chunks, { type: blobType });
      this.cleanupAudio();
      this.cleanupStream();
      const suppressed = this.suppressOnStop;
      this.recorder = null;
      this.chunks = [];
      const h = this.handlers;
      this.handlers = null;
      if (!suppressed && h) {
        const ext = extensionFor(blobType);
        h.onAutoStop(blob, `voice-${Date.now()}.${ext}`);
      }
    };
    this.recorder.onerror = (e) => {
      const evErr =
        (e as unknown as { error?: unknown }).error ?? new Error("recorder error");
      const err = evErr instanceof Error ? evErr : new Error(String(evErr));
      this.suppressOnStop = true;
      this.cleanupAudio();
      this.cleanupStream();
      const h = this.handlers;
      this.handlers = null;
      h?.onError?.(err);
    };

    try {
      // Kleiner Timeslice damit ondataavailable regelmäßig feuert (250 ms) —
      // sichert auf iOS, dass beim Stop genug Chunks da sind und nichts verloren geht.
      this.recorder.start(250);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      this.cleanupStream();
      this.recorder = null;
      handlers.onError?.(new Error(`Recorder-Start: ${e.message}`));
      return;
    }

    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioCtx = new Ctx();
      // iOS sperrt AudioContext bis User-Gesture — wir kommen aus einem Tap,
      // also resume() jetzt aufrufen damit der Context aktiv ist.
      if (this.audioCtx.state === "suspended") {
        this.audioCtx.resume().catch(() => {});
      }
      const src = this.audioCtx.createMediaStreamSource(this.mediaStream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 1024;
      this.analyser.smoothingTimeConstant = 0.55;
      src.connect(this.analyser);
    } catch (err) {
      // AnalyserNode ist "nice to have" für Orb-Animation — ohne ist's nicht schlimm.
      console.warn("AnalyserNode konnte nicht erzeugt werden:", err);
    }

    this.hasSpoken = false;
    this.silenceStartAt = null;
    this.speechOnsetAt = null;
    this.startedAt = performance.now();
    this.tickLoop();
  }

  /** Stoppt und liefert das Audio per onAutoStop. */
  finish(): void {
    if (!this.recorder) return;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    try {
      if (this.recorder.state === "recording") this.recorder.stop();
    } catch {
      // ignore
    }
  }

  /** Bricht ab — onAutoStop wird NICHT gefeuert. */
  cancel(): void {
    if (!this.recorder) return;
    this.suppressOnStop = true;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    try {
      if (this.recorder.state === "recording") this.recorder.stop();
    } catch {
      // ignore
    }
  }

  isActive(): boolean {
    return !!this.recorder;
  }

  private tickLoop(): void {
    const buf = new Float32Array(this.analyser?.fftSize ?? 1024);
    const tick = () => {
      if (!this.analyser) {
        // Ohne Analyser kein RMS — nur Timeout-Logik für continuous.
        const now = performance.now();
        if (this.mode === "continuous" && now - this.startedAt > this.MAX_RECORD_MS) {
          this.handlers?.onTimeout?.();
          this.suppressOnStop = true;
          try {
            if (this.recorder?.state === "recording") this.recorder.stop();
          } catch {
            // ignore
          }
          return;
        }
        this.rafId = requestAnimationFrame(tick);
        return;
      }
      this.analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      this.handlers?.onLevel(rms);

      const now = performance.now();
      const elapsed = now - this.startedAt;

      if (this.mode === "continuous") {
        if (rms > this.SPEECH_THRESHOLD) {
          if (this.speechOnsetAt === null) this.speechOnsetAt = now;
          if (!this.hasSpoken && now - this.speechOnsetAt > this.ONSET_MS) {
            this.hasSpoken = true;
            this.handlers?.onSpeechStart?.();
          }
          this.silenceStartAt = null;
        } else {
          this.speechOnsetAt = null;
          if (this.hasSpoken) {
            if (this.silenceStartAt === null) this.silenceStartAt = now;
            if (now - this.silenceStartAt > this.SILENCE_MS) {
              this.finish();
              return;
            }
          }
        }

        if (elapsed > this.MAX_RECORD_MS) {
          if (this.hasSpoken) {
            this.finish();
          } else {
            this.suppressOnStop = true;
            if (this.rafId) cancelAnimationFrame(this.rafId);
            this.rafId = 0;
            try {
              if (this.recorder && this.recorder.state === "recording")
                this.recorder.stop();
            } catch {
              // ignore
            }
            this.handlers?.onTimeout?.();
          }
          return;
        }
      } else {
        // manual / PTT: kein Auto-Stop, nur Level publishen.
        // Sicherheitsnetz: hart cappen bei 60 s.
        if (elapsed > 60_000) {
          this.finish();
          return;
        }
      }

      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private cleanupAudio(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.analyser = null;
    if (this.audioCtx) {
      this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
  }

  private cleanupStream(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
  }
}

/**
 * AnalyserNode an ein HTMLAudioElement hängen, um RMS während der TTS-Wiedergabe
 * für die Orb-Animation zu messen. Liefert eine read() Funktion + Cleanup.
 */
export function attachAudioAnalyser(audio: HTMLAudioElement): {
  read(): number;
  dispose(): void;
} {
  let ctx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let buf: Float32Array | null = null;
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const src = ctx.createMediaElementSource(audio);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.4;
    src.connect(analyser);
    analyser.connect(ctx.destination);
    buf = new Float32Array(analyser.fftSize);
  } catch (err) {
    console.warn("Audio-Analyser konnte nicht erstellt werden:", err);
  }
  return {
    read(): number {
      if (!analyser || !buf) return 0;
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      return Math.sqrt(sum / buf.length);
    },
    dispose(): void {
      if (ctx) {
        ctx.close().catch(() => {});
        ctx = null;
      }
      analyser = null;
      buf = null;
    },
  };
}
